// Typed barrel over import.meta.env. All environment-specific behaviour
// flows through this module — feature code never reads import.meta.env
// directly (per docs/architecture/frontend-structure.md §9).

// LogLevel typing comes from the shared observability package so env and the
// logger agree on the level union.
import { LOG_LEVELS, type LogLevel } from '@neon-one/frontend-observability';

// Derive the WS base from window.location so the bidder web works when
// loaded from a phone over the LAN (e.g. http://10.0.0.x:5175). The
// hardcoded localhost default failed in that case because the phone
// tried to open a socket against its own loopback.
const defaultWsBaseUrl = (): string => {
  if (typeof window === 'undefined') return 'ws://localhost:3002/v1';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.hostname}:3002/v1`;
};

// Frontend log verbosity (GT1-414). Defaults to quieter INFO for production
// builds and verbose DEBUG in dev; override per stage with VITE_LOG_LEVEL.
// Typed as the LogLevel union so consumers never need casts.
const isLogLevel = (value: string): value is LogLevel =>
  (LOG_LEVELS as readonly string[]).includes(value);
const defaultLogLevel = (): LogLevel => (import.meta.env.PROD ? 'info' : 'debug');
const resolveLogLevel = (): LogLevel => {
  const raw = (import.meta.env.VITE_LOG_LEVEL ?? '').toLowerCase();
  return isLogLevel(raw) ? raw : defaultLogLevel();
};

export const env = {
  apiBaseUrl: (import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/+$/, ''),
  wsBaseUrl: (import.meta.env.VITE_WS_BASE_URL ?? defaultWsBaseUrl()).replace(/\/+$/, ''),
  devFallbackSlug: import.meta.env.VITE_DEV_FALLBACK_SLUG ?? 'spring-gala-2026',
  demoMode: import.meta.env.VITE_DEMO_MODE === 'true',
  clientErrorReporting: import.meta.env.VITE_CLIENT_ERROR_REPORTING !== 'false',
  // Opt-in, so a build path that forgets the flag ships without it. Always on
  // under `vite dev`. Only the dev and uat deploy workflows set it to 'true'.
  feedbackWidget: import.meta.env.DEV || import.meta.env.VITE_FEEDBACK_WIDGET === 'true',
  logLevel: resolveLogLevel(),
} as const;

export type Env = typeof env;
